import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAssessmentService } from '../services/assessmentService';
import { logMessage } from '../utils/logMessage';

const MAX_ASSIGN_RETRIES = 2;
const RETRY_DELAY_MS = 600;

const QuickAssign: React.FC = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { quickAssign } = useAssessmentService();
  const [isAssigning, setIsAssigning] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const requestIdRef = useRef(0);
  const quickAssignRef = useRef(quickAssign);

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const hasValidAssignment = (assignment: any): assignment is { assignmentId: number } => {
    return Number.isFinite(assignment?.assignmentId) && Number(assignment.assignmentId) > 0;
  };

  useEffect(() => {
    quickAssignRef.current = quickAssign;
  }, [quickAssign]);

  useEffect(() => {
    if (!assessmentId) {
      logMessage('[QuickAssign] Missing assessmentId in URL', 'warn');
      setIsAssigning(false);
      setErrorMessage('Invalid assignment link. Please request a new assignment link.');
      return;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    setIsAssigning(true);
    setErrorMessage(null);

    const runQuickAssign = async () => {
      let lastError: Error | null = null;

      logMessage('[QuickAssign] Assignment request started', 'info', {
        assessmentId,
        requestId: currentRequestId,
        maxRetries: MAX_ASSIGN_RETRIES,
      });

      for (let attempt = 0; attempt <= MAX_ASSIGN_RETRIES; attempt += 1) {
        try {
          logMessage('[QuickAssign] Attempting quick assign', 'info', {
            assessmentId,
            requestId: currentRequestId,
            attempt: attempt + 1,
          });

          const assessment = await quickAssignRef.current(assessmentId);
          if (!hasValidAssignment(assessment)) {
            throw new Error('Assignment response did not include a valid assignment id.');
          }

          if (requestIdRef.current !== currentRequestId) {
            logMessage('[QuickAssign] Assignment request superseded by newer request', 'warn', {
              assessmentId,
              requestId: currentRequestId,
            });
            return;
          }

          logMessage('[QuickAssign] Assignment request succeeded', 'info', {
            assessmentId,
            requestId: currentRequestId,
            assignmentId: assessment.assignmentId,
            attempt: attempt + 1,
          });

          navigate(`/call/${assessmentId}`, {
            state: {
              fromQuickAssign: true,
              assignmentId: assessment.assignmentId,
            },
          });
          return;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown quick assign error';
          lastError = new Error(message);
          const hasRetriesLeft = attempt < MAX_ASSIGN_RETRIES;

          logMessage('[QuickAssign] Assignment request failed', 'warn', {
            assessmentId,
            requestId: currentRequestId,
            attempt: attempt + 1,
            hasRetriesLeft,
            errorMessage: message,
          });

          if (hasRetriesLeft) {
            await sleep(RETRY_DELAY_MS * (attempt + 1));
          }
        }
      }

      if (requestIdRef.current !== currentRequestId) {
        logMessage('[QuickAssign] Final failure ignored due to newer request', 'warn', {
          assessmentId,
          requestId: currentRequestId,
        });
        return;
      }

      logMessage('[QuickAssign] Assignment request exhausted retries', 'error', {
        assessmentId,
        requestId: currentRequestId,
        errorMessage: lastError?.message,
      });

      setIsAssigning(false);
      setErrorMessage(
        `Error assigning user. ${lastError?.message ?? 'Please try again or contact support.'}`
      );
    };

    runQuickAssign();
  }, [assessmentId, navigate, retryTrigger]);

  const handleRetry = () => {
    if (isAssigning) return;
    logMessage('[QuickAssign] Manual retry requested', 'info', {
      assessmentId,
      retryTrigger: retryTrigger + 1,
    });
    setRetryTrigger((prev) => prev + 1);
  };

  return (
    <div style={{ padding: '24px', textAlign: 'center' }}>
      {isAssigning && <div>Assigning assessment, please wait...</div>}
      {!isAssigning && errorMessage && (
        <div>
          <div style={{ marginBottom: '12px' }}>{errorMessage}</div>
          <button type="button" onClick={handleRetry}>
            Retry Assignment
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickAssign;